import jsPDF from 'jspdf';
import moment from 'moment';
import React, { useState } from 'react';
import 'jspdf-autotable';
import styled from 'styled-components';
import { PrintButton } from 'components/PrintButton';
import checkbox from 'images/checkbox.png';
import { KartPassLogo } from 'images/kartpass-logo';
import { Logo } from 'images/logo';
import { TicketHossLogo } from 'images/tickethoss-logo';
import 'moment-timezone';
import { useGetBoxOfficeReport } from '../gql/mutation/useGetBoxOfficeReport.mutation';

const ButtonContainer = styled.div`
  & > button {
    width: 100%;

    @media screen and (min-width: 861px) {
      width: auto;
    }
  }
`;

const getOverviewHeaders = () => {
  return [
    ['Cash', 'Card', 'No. of Transactions', 'No. of Tickets', 'Total Revenue'],
  ];
};

const getOverviewColumns = () => {
  return [
    { dataKey: 'cash', header: 'Cash' },
    { dataKey: 'card', header: 'Card' },
    { dataKey: 'transactions', header: 'No. of Transactions' },
    { dataKey: 'tickets', header: 'No. of Tickets' },
    { dataKey: 'revenue', header: 'Total Revenue' },
  ];
};

const buildOverviewData = (seller) => {
  return [
    [
      seller.cash,
      seller.card,
      seller.num_transactions,
      seller.num_tickets,
      seller.total_revenue,
    ],
  ];
};

const getHeaders = () => {
  return [['Name', 'Quantity', 'Price']];
};

const getColumns = () => {
  return [
    { dataKey: 'name', header: 'Name' },
    { dataKey: 'qty', header: 'Quantity' },
    { dataKey: 'price', header: 'Price' },
  ];
};

const buildData = (ticketSales) => {
  return ticketSales.map((sales) => [sales.name, sales.qty, sales.price]);
};

export const BoxOfficeReportPrint = ({
  date,
  eventId,
  text,
  textStyle,
  button,
  reportData,
}) => {
  const isKartPass = 'kartpass' === process.env.REACT_APP_PLATFORM;
  const isTicketHoss = 'tickethoss' === process.env.REACT_APP_PLATFORM;

  const getBoxOfficeReport = useGetBoxOfficeReport();
  const [disabled] = useState(false);

  const getData = () => {
    return getBoxOfficeReport(eventId)
      .then((response) => {
        return response.data.getPOSRegistersReport;
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.log(e);
      });
  };

  const exportPDF = async () => {
    const report = reportData || (await getData());
    const unit = 'pt';
    const size = 'A4'; // Use A1, A2, A3 or A4
    const orientation = 'landscape'; // portrait or landscape

    const event = report.event;
    const sellers = report.data || [];

    const doc = new jsPDF(orientation, unit, size, true);

    doc.setFontSize(15);
    doc.setFontStyle('bold');

    const title = date;

    const pageSize = doc.internal.pageSize;
    const pageWidth = pageSize.width ? pageSize.width : pageSize.getWidth();
    const pageHeight = pageSize.height ?? pageSize.getHeight();

    const headerText = doc.splitTextToSize(title, pageWidth - 650, {});

    const trackOrSeriesName =
      event.track?.name ??
      (event.series?.length > 0 ? event.series[0].name : '');
    doc.text(headerText, 40, 40);
    doc.text(
      event?.name.length > 75 ? event?.name.slice(0, 75) : event?.name,
      40,
      60
    );
    let Y = 60;
    if (event?.name.length > 75) {
      Y += 20;
      doc.text(event?.name.slice(75, 150), 40, Y);
    }

    if (event?.account_name) {
      Y += 20;
      doc.text(event?.account_name, 40, Y);
    }

    if (trackOrSeriesName) {
      Y += 20;
      doc.text(trackOrSeriesName, 40, Y);
    }

    Y += 20;
    doc.setTextColor('#000');
    doc.text('Box Office Report', 620, 40);

    if (checkbox) {
      if (isKartPass) {
        doc.addImage(KartPassLogo, 'PNG', 650, 40, 100, 75, '', 'FAST');
      } else if (isTicketHoss) {
        doc.addImage(TicketHossLogo, 'PNG', 660, 40, 100, 54, '', 'FAST');
      } else {
        doc.addImage(Logo, 'PNG', 660, 20, 75, 75, '', 'FAST');
      }
    }

    Y += 30;

    sellers.forEach((seller) => {
      // Start a new page if there isn't enough room for the user's header
      if (Y > pageHeight - 150) {
        doc.addPage();
        Y = 40;
      }

      doc.setFontSize(13);
      doc.setFontStyle('bold');
      doc.text(`User: ${seller?.user?.formatted_name ?? 'Unknown'}`, 40, Y);
      Y += 18;
      doc.setFontSize(11);
      doc.text(
        `Tickets Sold - ${(seller.ticket_breakdown_data || []).reduce(
          (acc, item) => acc + item.qty,
          0
        )}`,
        40,
        Y
      );
      Y += 10;

      doc.autoTable({
        startY: Y,
        rowPageBreak: 'avoid',
        columns: getOverviewColumns(),
        head: getOverviewHeaders(),
        body: buildOverviewData(seller),
        styles: {
          fontStyle: 'bold',
        },
        tableWidth: 'auto',
        headStyles: {
          fillColor: isKartPass ? '#00001F' : '#fa4616',
        },
      });

      doc.autoTable({
        startY: doc.lastAutoTable.finalY + 15,
        rowPageBreak: 'avoid',
        columns: getColumns(),
        head: getHeaders(),
        body: buildData(seller.ticket_breakdown_data || []),
        styles: {
          fontStyle: 'bold',
        },
        tableWidth: 'auto',
        headStyles: {
          fillColor: isKartPass ? '#00001F' : '#fa4616',
        },
      });

      Y = doc.lastAutoTable.finalY + 40;
    });

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 0; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(10);
      doc.setFontStyle('normal');

      const now = moment();
      const momentInSpecificTimezone = now.tz('America/New_York');
      const dateTimeStamp =
        momentInSpecificTimezone.format('MMM DD - YYYY h:mm A').toUpperCase() +
        ' EST';
      doc.text(`Exported ${dateTimeStamp}`, pageWidth - 40, pageHeight - 20, {
        align: 'right',
      });
    }
    // Set typography back to default
    doc.setFontSize(15);
    doc.setFontStyle('bold');

    doc.setProperties({
      title: `${date} - ${event.name} - Box Office Report.pdf`,
      subject: `${date} - ${event.name} - Box Office Report`,
      author: process.env.REACT_APP_PLATFORM,
    });
    return {
      ...doc,
      fileName: `${date} - ${event.name} - Box Office Report.pdf`,
    };
  };

  return (
    <ButtonContainer>
      <PrintButton
        buildPdf={exportPDF}
        text={text}
        disabled={disabled}
        variant={'minimal'}
        textStyle={textStyle}
        button={button}
      />
    </ButtonContainer>
  );
};
